import { CALENDAR_FREQUENCIES, MODES } from './constants';
import { deriveCalendarSubRuleFromAnchor } from './anchor';
import { deriveWeekdaysFromAnchor, parseWeekdaysArray } from './weekdays';
import { isNeverRule } from './describeRule';
import {
  canonicalizeRepeatRule,
  LEGACY_FREQUENCY_MAP,
  DEFAULT_INTERVAL,
  parseInterval,
} from './canonicalize';

export function normalizeRepeatRule(rule, anchorDate) {
  if (isNeverRule(rule)) return null;

  const frequency = String(rule.frequency || '').trim().toLowerCase();
  const next = { frequency, interval: parseInterval(rule.interval) ?? DEFAULT_INTERVAL };

  if (frequency === 'weekly') {
    const parsed = parseWeekdaysArray(rule.weekdays);
    next.weekdays =
      parsed && parsed.length > 0 ? parsed : deriveWeekdaysFromAnchor(anchorDate);
  }

  if (CALENDAR_FREQUENCIES.includes(frequency)) {
    const derived = deriveCalendarSubRuleFromAnchor(anchorDate);
    const mode = MODES.includes(rule.mode) ? rule.mode : 'dayOfMonth';
    next.mode = mode;
    if (mode === 'dayOfMonth') {
      next.dayOfMonth = Number.isInteger(rule.dayOfMonth) ? rule.dayOfMonth : derived.dayOfMonth;
    } else {
      next.nth = rule.nth === 'last' ? 'last' : (Number.isInteger(rule.nth) ? rule.nth : derived.nth);
      next.weekday = Number.isInteger(rule.weekday) ? rule.weekday : derived.weekday;
    }
  }

  return canonicalizeRepeatRule(next);
}

export function buildRepeatRuleForFrequency(frequency, anchorDate, existingRule) {
  const raw = String(frequency || '').trim().toLowerCase();
  if (!raw || raw === 'never') return null;

  const legacy = LEGACY_FREQUENCY_MAP[raw];
  const freq = legacy ? legacy.frequency : raw;
  const existingCanon = existingRule ? canonicalizeRepeatRule(existingRule) : null;
  const sameFreq =
    existingCanon && String(existingCanon.frequency || '').trim().toLowerCase() === freq;
  const interval = sameFreq && parseInterval(existingCanon.interval) != null
    ? parseInterval(existingCanon.interval)
    : legacy
      ? legacy.interval
      : DEFAULT_INTERVAL;

  const base = { frequency: freq, interval };

  if (freq === 'weekly') {
    const parsed = sameFreq ? parseWeekdaysArray(existingRule?.weekdays) : null;
    base.weekdays =
      parsed && parsed.length > 0 ? parsed : deriveWeekdaysFromAnchor(anchorDate);
    return canonicalizeRepeatRule(base);
  }

  if (!CALENDAR_FREQUENCIES.includes(freq)) return canonicalizeRepeatRule(base);

  const derived = deriveCalendarSubRuleFromAnchor(anchorDate);
  const mode =
    sameFreq && MODES.includes(existingRule.mode) ? existingRule.mode : 'dayOfMonth';

  if (mode === 'nthWeekday') {
    return canonicalizeRepeatRule({
      ...base,
      mode: 'nthWeekday',
      nth: sameFreq && existingRule.nth != null ? existingRule.nth : derived.nth,
      weekday:
        sameFreq && Number.isInteger(existingRule.weekday)
          ? existingRule.weekday
          : derived.weekday,
    });
  }

  return canonicalizeRepeatRule({
    ...base,
    mode: 'dayOfMonth',
    dayOfMonth:
      sameFreq && Number.isInteger(existingRule.dayOfMonth)
        ? existingRule.dayOfMonth
        : derived.dayOfMonth,
  });
}

export function getMonthlySubOptions(anchorDate) {
  const derived = deriveCalendarSubRuleFromAnchor(anchorDate);
  const nthLabel = derived.nth === 'last' ? 'last' : `${derived.nth}${['st', 'nd', 'rd', 'th'][Math.min(derived.nth - 1, 3)]}`;
  return [
    {
      mode: 'dayOfMonth',
      label: `Day ${derived.dayOfMonth} of each month`,
      rule: { mode: 'dayOfMonth', dayOfMonth: derived.dayOfMonth },
    },
    {
      mode: 'nthWeekday',
      label: `The ${nthLabel} ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][derived.weekday]} of each month`,
      rule: { mode: 'nthWeekday', nth: derived.nth, weekday: derived.weekday },
    },
  ];
}
